import { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

export const ProfileEditor = ({ open, onOpenChange, profile, onSaved }) => {
  const [avatars, setAvatars] = useState([]);
  const [form, setForm] = useState({ name: "", avatar: "", is_kids: false });
  const [busy, setBusy] = useState(false);
  const isNew = !profile;

  useEffect(() => { api.get("/profiles/avatars").then((r) => setAvatars(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (open) setForm(profile ? { name: profile.name, avatar: profile.avatar, is_kids: profile.is_kids, maturity: profile.maturity || "all" } : { name: "", avatar: avatars[1] || avatars[0] || "", is_kids: false, maturity: "all" });
  }, [open, profile, avatars]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Please enter a profile name");
    setBusy(true);
    try {
      if (isNew) await api.post("/profiles", form);
      else await api.put(`/profiles/${profile.id}`, form);
      toast.success(isNew ? "Profile created" : "Profile updated");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/profiles/${profile.id}`);
      toast.success("Profile deleted");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-editor-dialog" className="max-w-lg bg-[#0B0B0D] border border-[#D4AF37]/30 text-white rounded-2xl">
        <DialogTitle className="font-display text-2xl gold-text">{isNew ? "Add Profile" : "Edit Profile"}</DialogTitle>
        <div className="space-y-6 pt-2">
          <div>
            <p className="eyebrow mb-3">Choose avatar</p>
            <div className="flex flex-wrap gap-3">
              {avatars.map((a, i) => (
                <button key={a} data-testid={`avatar-option-${i}`} onClick={() => setForm({ ...form, avatar: a })} className={`rounded-xl overflow-hidden transition-[transform,box-shadow] ${form.avatar === a ? "ring-2 ring-[#FFD86A] shadow-[0_0_20px_rgba(255,216,106,0.45)] scale-105" : "ring-1 ring-white/10 opacity-70 hover:opacity-100"}`}>
                  <img src={a} alt="" className="h-16 w-16 object-cover" />
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="eyebrow mb-2 block">Name</span>
            <input data-testid="profile-name-input" maxLength={24} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-[#D4AF37]/25 bg-black/50 px-4 py-3 outline-none focus:border-[#FFD86A]" placeholder="Profile name" />
          </label>
          {!profile?.is_owner && (
            <div className="flex items-center justify-between rounded-xl border border-[#D4AF37]/20 px-4 py-3">
              <div>
                <p className="font-semibold">Kids profile</p>
                <p className="text-xs text-[#8B8B8B]">Only shows titles rated for younger viewers.</p>
              </div>
              <Switch data-testid="profile-kids-switch" checked={form.is_kids} onCheckedChange={(v) => setForm({ ...form, is_kids: v })} className="data-[state=checked]:bg-[#D4AF37]" />
            </div>
          )}
          {!form.is_kids && (
            <label className="flex items-center justify-between gap-4 rounded-xl border border-[#D4AF37]/20 px-4 py-3">
              <span><span className="block font-semibold">Maturity setting</span><span className="text-xs text-[#8B8B8B]">Teen hides titles marked mature.</span></span>
              <select data-testid="profile-maturity-select" value={form.maturity || "all"} onChange={(e) => setForm({ ...form, maturity: e.target.value })} className="rounded-lg border border-[#D4AF37]/30 bg-black px-3 py-2 text-sm text-white">
                <option value="all">All maturity ratings</option>
                <option value="teen">Teen (13+)</option>
              </select>
            </label>
          )}
          <div className="flex items-center gap-3 pt-2">
            {!isNew && !profile.is_owner && (
              <button data-testid="profile-delete-btn" onClick={remove} disabled={busy} className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-[#ff6b6b] hover:bg-[#E50914]/10 transition-colors">
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            )}
            <button data-testid="profile-cancel-btn" onClick={() => onOpenChange(false)} className="btn-ghost-gold ml-auto px-5 py-2.5 text-sm">Cancel</button>
            <button data-testid="profile-save-btn" onClick={save} disabled={busy} className="btn-gold px-6 py-2.5 text-sm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
